import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, CircleNotch, FileArrowDown, Sparkle } from "@phosphor-icons/react";
import { Field, Input, Select, Btn } from "../epbcs/common";

const FORMATS = ["pdf", "xlsx", "csv", "html", "xml", "rtf"];

export const ReportRunner = ({ path, name, initialValues, initialFormat, onAnalyze }) => {
  const [params, setParams] = React.useState(null);
  const [values, setValues] = React.useState({});
  const [formats, setFormats] = React.useState(FORMATS);
  const [fmt, setFmt] = React.useState(initialFormat || "pdf");
  const [running, setRunning] = React.useState(false);
  const [loadErr, setLoadErr] = React.useState("");

  React.useEffect(() => {
    setParams(null); setValues({}); setLoadErr("");
    api.get("/reporting/report/parameters", { params: { path } })
      .then((r) => {
        setParams(r.data.parameters || []);
        const allowed = (r.data.formats || []).length ? r.data.formats : FORMATS;
        setFormats(allowed);
        const want = initialFormat || r.data.default_format || "pdf";
        setFmt(allowed.includes(want) ? want
          : (allowed.includes(r.data.default_format) ? r.data.default_format : allowed[0]));
        const init = {};
        (r.data.parameters || []).forEach((p) => { if (p.default_value) init[p.name] = p.default_value; });
        setValues({ ...init, ...(initialValues || {}) });
      })
      .catch((e) => {
        setParams([]);
        setLoadErr(e?.response?.data?.detail || "Could not load report parameters");
      });
  }, [path]);

  const buildPayload = () => {
    const payload = {};
    (params || []).forEach((p) => {
      const v = values[p.name];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return;
      if (Array.isArray(v)) payload[p.name] = v;
      else if (p.multi_values && !p.lov?.length) payload[p.name] = String(v).split(",").map((s) => s.trim()).filter(Boolean);
      else payload[p.name] = v;
    });
    return payload;
  };

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.post("/reporting/report/run",
        { path, format: fmt, parameters: buildPayload() }, { responseType: "blob", timeout: 240000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''(.+)/);
      a.href = url;
      a.download = m ? decodeURIComponent(m[1]) : `${name || "report"}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report generated and downloaded");
    } catch (e) {
      let msg = "Report run failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="report-runner">
      <div className="flex items-center gap-2 mb-3">
        <FileArrowDown size={16} weight="bold" className="text-[#C8A24B]" />
        <div className="text-[13px] font-semibold text-[#0F172A] truncate">{name || path}</div>
      </div>
      <div className="text-[11px] text-slate-500 font-mono mb-4 break-all">{path}</div>
      {params === null && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500 py-3" data-testid="runner-params-loading">
          <CircleNotch size={14} className="animate-spin" /> Fetching report parameters from Fusion…
        </div>
      )}
      {loadErr && (
        <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 px-3 py-2 mb-3" data-testid="runner-params-error">
          {loadErr} — you can still attempt to run the report without parameters.
        </div>
      )}
      {params !== null && params.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {params.map((p) => {
            const hasLov = (p.lov || []).length > 0;
            if (hasLov && p.multi_values) {
              const sel = Array.isArray(values[p.name]) ? values[p.name] : [];
              return (
                <Field key={p.name} label={`${p.label || p.name} (multi — ${sel.length ? `${sel.length} selected` : "all"})`}>
                  <select multiple size={5} data-testid={`runner-param-${p.name}`}
                    className="w-full border border-slate-300 px-2 py-1.5 text-[12px] bg-white rounded-sm focus:border-[#0B1E3F] focus:ring-1 focus:ring-[#0B1E3F] outline-none"
                    value={sel}
                    onChange={(e) => {
                      const picked = Array.from(e.target.selectedOptions).map((o) => o.value);
                      setValues((v) => ({ ...v, [p.name]: picked }));
                    }}>
                    {p.lov.map((o, i) => (
                      <option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
              );
            }
            if (hasLov) {
              return (
                <Field key={p.name} label={p.label || p.name}>
                  <Select data-testid={`runner-param-${p.name}`}
                    value={values[p.name] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))}>
                    <option value="">— select —</option>
                    {p.lov.map((o, i) => (
                      <option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>
              );
            }
            return (
              <Field key={p.name} label={`${p.label || p.name}${p.multi_values ? " (comma-sep)" : ""}`}>
                <Input
                  data-testid={`runner-param-${p.name}`}
                  placeholder={p.data_type || "value"}
                  value={values[p.name] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))}
                />
              </Field>
            );
          })}
        </div>
      )}
      {params !== null && params.length === 0 && !loadErr && (
        <div className="text-[12px] text-slate-500 mb-4" data-testid="runner-no-params">
          This report has no input parameters.
        </div>
      )}
      <div className="flex items-end gap-3">
        <div className="w-32">
          <Field label="Format">
            <Select data-testid="runner-format-select" value={fmt} onChange={(e) => setFmt(e.target.value)}>
              {formats.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
            </Select>
          </Field>
        </div>
        <Btn data-testid="runner-run-btn" onClick={run} disabled={running || params === null}>
          <span className="flex items-center gap-2">
            {running ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} weight="fill" />}
            {running ? "Running…" : "Run & Download"}
          </span>
        </Btn>
        {onAnalyze && (
          <button data-testid="runner-analyze-btn"
            onClick={() => onAnalyze(buildPayload())}
            disabled={running || params === null}
            className="flex items-center gap-2 border border-[#C8A24B] text-[#0B1E3F] px-4 py-2 text-[12px] font-semibold tracking-wide hover:bg-[#C8A24B]/10 transition-colors disabled:opacity-50 rounded-sm">
            <Sparkle size={14} weight="fill" className="text-[#C8A24B]" />
            Run &amp; Summarize
          </button>
        )}
      </div>
    </div>
  );
};

export const SqlViewer = ({ path }) => {
  const [queries, setQueries] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    setQueries(null); setErr("");
    api.get("/reporting/datamodel/sql", { params: { path } })
      .then((r) => setQueries(r.data.queries || []))
      .catch((e) => { setQueries([]); setErr(e?.response?.data?.detail || "Could not extract SQL"); });
  }, [path]);

  return (
    <div data-testid="sql-viewer">
      <div className="text-[11px] text-slate-500 font-mono mb-3 break-all">{path}</div>
      {queries === null && (
        <div className="text-[12px] text-slate-500 py-2">Downloading data model and extracting SQL…</div>
      )}
      {err && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 px-3 py-2">{err}</div>}
      {queries !== null && queries.length === 0 && !err && (
        <div className="text-[12px] text-slate-500">No SQL datasets found inside this data model.</div>
      )}
      {(queries || []).map((q, i) => (
        <div key={i} className="mb-3 border border-slate-200" data-testid={`sql-query-${i}`}>
          <div className="flex items-center justify-between px-3 py-1.5 bg-[#0B1E3F] text-white">
            <span className="text-[11px] font-semibold tracking-wide">{q.dataset}</span>
            {q.data_source && <span className="text-[10px] text-[#C8A24B] font-mono">{q.data_source}</span>}
          </div>
          <pre className="p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap bg-slate-50 text-slate-800 max-h-72 overflow-auto">
            {q.sql}
          </pre>
        </div>
      ))}
    </div>
  );
};
